// Employee identity colors — 15-color pastel pool (spec §8 / §5.3).
// Each employee is assigned one stable color used for the avatar, the filter
// chip, and the "Given by" tag. The five named seeds come straight from the
// spec; everyone else is assigned deterministically from the pool by a stable
// hash of their userId (falling back to name) so the colour never shifts
// between renders or sessions.

// The 15-color pastel pool. The first five entries are the spec's named seeds.
export const PASTEL_POOL = [
    { fill: '#B9D4FF', ink: '#1E40AF' }, // blue        (ASHLEY)
    { fill: '#C9F0D6', ink: '#15803D' }, // green       (NICK)
    { fill: '#FFD9C2', ink: '#C2410C' }, // orange      (LINA)
    { fill: '#E4D2FF', ink: '#6D28D9' }, // purple      (LA JEFA)
    { fill: '#FFC9DE', ink: '#BE185D' }, // pink        (BIANCA)
    { fill: '#BAE6FD', ink: '#0369A1' }, // sky
    { fill: '#FDE68A', ink: '#92400E' }, // amber
    { fill: '#C7F9E8', ink: '#0F766E' }, // teal
    { fill: '#E2E8F0', ink: '#475569' }, // slate
    { fill: '#FBCFE8', ink: '#9D174D' }, // rose
    { fill: '#DDD6FE', ink: '#5B21B6' }, // violet
    { fill: '#BBF7D0', ink: '#166534' }, // emerald
    { fill: '#FED7AA', ink: '#9A3412' }, // peach
    { fill: '#A5F3FC', ink: '#155E75' }, // cyan
    { fill: '#FEF08A', ink: '#854D0E' }, // yellow
];

// Spec's stable name → colour seeds.
const NAMED_SEEDS = {
    ASHLEY: PASTEL_POOL[0],
    NICK: PASTEL_POOL[1],
    LINA: PASTEL_POOL[2],
    'LA JEFA': PASTEL_POOL[3],
    BIANCA: PASTEL_POOL[4],
};

const FALLBACK = { fill: '#E2E8F0', ink: '#475569' };

// djb2-ish stable string hash → non-negative int.
const hashString = (str) => {
    let hash = 5381;
    const s = String(str || '');
    for (let i = 0; i < s.length; i++) {
        hash = ((hash << 5) + hash + s.charCodeAt(i)) | 0;
    }
    return Math.abs(hash);
};

// Parse a #RGB / #RRGGBB hex string into [r,g,b], or null if not a hex.
const hexToRgb = (hex) => {
    if (typeof hex !== 'string') return null;
    let h = hex.trim().replace(/^#/, '');
    if (h.length === 3) h = h.split('').map((c) => c + c).join('');
    if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
    return [
        parseInt(h.slice(0, 2), 16),
        parseInt(h.slice(2, 4), 16),
        parseInt(h.slice(4, 6), 16),
    ];
};

// Pick a readable ink (dark navy or white) for text/initials on a given
// background, based on its perceived luminance.
const readableInk = (hex) => {
    const rgb = hexToRgb(hex);
    if (!rgb) return '#101935';
    const [r, g, b] = rgb;
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.6 ? '#101935' : '#ffffff';
};

// Build identity meta from an explicit operator-chosen colour (employeeColor
// from user settings). Falls back to null when the value isn't a usable hex.
export const metaFromColor = (color) => {
    const rgb = hexToRgb(color);
    if (!rgb) return null;
    return { fill: color, ink: readableInk(color) };
};

/**
 * Resolve an employee's identity colour.
 * Prefers the operator-chosen employeeColor (matching the rest of the app);
 * falls back to the stable pastel pool when no colour is set.
 * @param {string} key   stable identity key (prefer userId)
 * @param {string} name  display name (used for the named seeds)
 * @param {string} color optional employeeColor hex from user settings
 */
export const empMeta = (key, name, color) => {
    const fromColor = metaFromColor(color);
    if (fromColor) return fromColor;
    const upper = (name || '').trim().toUpperCase();
    if (upper && NAMED_SEEDS[upper]) return NAMED_SEEDS[upper];
    const seed = key || name;
    if (!seed) return FALLBACK;
    return PASTEL_POOL[hashString(seed) % PASTEL_POOL.length];
};

export const displayName = (name) => (name || '').trim().toUpperCase();

export const initialOf = (name) => {
    const n = (name || '').trim();
    return n ? n.charAt(0).toUpperCase() : '?';
};
